import {
  FaEnvelope,
  FaGithub,
  FaLinkedin,
} from 'react-icons/fa';

import type {
  ContactInfo,
  Project,
  SocialLink,
} from '../types';

export const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
] as const;

export const SOCIAL_LINKS: SocialLink[] = [
  { icon: FaGithub, url: 'https://github.com/davidagustin', label: 'GitHub' },
  {
    icon: FaLinkedin,
    url: 'https://www.linkedin.com/in/davidsyagustin/',
    label: 'LinkedIn',
  },
  { icon: FaEnvelope, url: 'mailto:davidsyagustin@gmail.com', label: 'Email' },
];

export const CONTACT_INFO: ContactInfo[] = [
  {
    icon: FaEnvelope,
    label: 'Email',
    value: 'davidsyagustin@gmail.com',
    url: 'mailto:davidsyagustin@gmail.com',
    color: '#333',
  },
  {
    icon: FaLinkedin,
    label: 'LinkedIn',
    value: 'davidsyagustin',
    url: 'https://www.linkedin.com/in/davidsyagustin/',
    color: '#0077B5',
  },
  {
    icon: FaGithub,
    label: 'GitHub',
    value: 'davidagustin',
    url: 'https://github.com/davidagustin',
    color: '#333',
  },
];

export const PROJECTS: Project[] = [
  {
    id: 1,
    title: 'Coding Drills',
    description:
      'Interactive learning platform for mastering programming languages through hands-on practice. Thousands of problems across 25 languages and 4 frontend frameworks, with real-time code execution, algorithm visualizations, AI mock interviews, regex training, and an algorithm pattern quiz.',
    technologies: ['Next.js 16', 'React 19', 'TypeScript 5', 'Tailwind CSS 4', 'Monaco Editor', 'Playwright'],
    githubUrl: 'https://github.com/davidagustin/coding-drills',
    liveUrl: 'https://coding-drills.vercel.app',
    category: 'Education Platform',
    features: [
      '25 Programming Languages',
      'AI Mock Interviews',
      'Algorithm Visualizations',
      'Real-time Code Execution',
    ],
    readmeHighlights: {
      overview: 'An interactive learning platform for mastering 25 programming languages through hands-on practice, featuring thousands of problems across language methods, algorithm patterns, frontend frameworks, and Building Blocks exercises with interactive visualizations.',
      highlights: [
        'Thousands of problems across 25 languages including JavaScript, Python, Java, C++, Go, Ruby, Rust, Swift, Kotlin, and more',
        '460 frontend training problems across React, Angular, Vue, and Native JavaScript with 616+ UI patterns',
        '170-problem algorithm pattern quiz for LeetCode-style pattern recognition',
        'Building Blocks: 35 structured algorithm exercises per language with Learn/Practice modes and interactive visualizations',
        'AI Exercise Tutor powered by WebLLM (Llama-3.1-8B) running fully in-browser with no API keys',
        'AI Mock Interview with 72 algorithm problems and 28 system design questions',
        '13,995 unit tests (Vitest) validating all 460 frontend drill samples',
        'Beginner Mode filter with ~270 curated fundamental problems across all languages',
      ],
      techDetails: 'Next.js 16 (App Router) with React 19, TypeScript 5, Tailwind CSS 4, Monaco Editor, Playwright E2E, Vitest, Zustand/localStorage for state, and WebLLM for offline AI.',
    },
  },
  {
    id: 2,
    title: 'StreamFlix',
    description:
      'Modern streaming platform with a Netflix/Disney+ inspired interface. Features movie browsing with genre-based carousels, hero sections with backdrops, real movie posters with robust image fallback systems, hover cards with ratings, and responsive dark-themed UI.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript', 'Tailwind CSS', 'AWS Amplify'],
    githubUrl: 'https://github.com/davidagustin/aws-amplify-practice',
    liveUrl: 'https://main.d1bolbr8do04cv.amplifyapp.com/',
    category: 'Streaming Platform',
    features: [
      'Genre-Based Carousels',
      'Hero Section with Backdrop',
      'Real Movie Posters',
      'Responsive Dark Theme',
    ],
    readmeHighlights: {
      overview: 'A modern streaming platform with a Netflix/Disney+ inspired interface featuring movie browsing, horizontal scrolling carousels, and a hero section with backdrop images. Deployed on AWS Amplify with global CDN.',
      highlights: [
        'Deployed on AWS Amplify with global CDN and worldwide distribution',
        'Diverse movie collection spanning Action/Adventure, Comedy, Drama, Sci-Fi/Fantasy with 20+ curated films',
        'Real movie posters from OMDb, TMDB, and Wikimedia with automatic fallback to styled placeholders',
        'Fixed navbar with scroll-triggered background and smooth transitions',
        'Full-screen hero section with gradient overlay for text readability',
        'Horizontal scrolling carousels with arrow navigation and Netflix-style hover animations',
      ],
      techDetails: 'Next.js 15 with React 19, TypeScript, Tailwind CSS, and Lucide React icons. Component-based architecture with modular Hero, Navbar, and MovieCarousel components.',
    },
  },
  {
    id: 3,
    title: 'Premium Auto Sales',
    description:
      'Professional car sales website built with Hugo for GitLab Pages. Features vehicle inventory with detailed listings, contact forms, multi-language support (English, French, Persian RTL), and SEO optimization.',
    technologies: ['Hugo', 'GitLab Pages', 'CSS3', 'Multi-language', 'SEO'],
    githubUrl: 'https://gitlab.com/davidagustin1-group/gitlab-hugo-practice-app',
    liveUrl: 'https://gitlab-hugo-practice-app-e1b201.gitlab.io/',
    category: 'E-commerce',
    features: [
      'Vehicle Inventory',
      'Multi-language Support',
      'Contact Forms',
      'SEO Optimized',
    ],
    readmeHighlights: {
      overview: 'Professional car sales website built with Hugo static site generator for GitLab Pages. Features vehicle inventory with detailed listings and multi-language support including English, French, and Persian with RTL layout.',
      highlights: [
        'Hugo static site generator for fast page loads and SEO optimization',
        'Multi-language support: English, French, and Persian (RTL)',
        'Vehicle inventory with detailed specifications and pricing',
        'Contact forms for dealer inquiries',
        'Deployed on GitLab Pages with CI/CD pipeline',
      ],
      techDetails: 'Hugo static site generator with Go templating, CSS3 for styling, and GitLab Pages for deployment with multi-language i18n configuration.',
    },
  },
  {
    id: 4,
    title: 'TechStore',
    description:
      'Comprehensive e-commerce platform for computer hardware enthusiasts. Features 8 product categories, advanced shopping cart with real-time updates, search and filtering, product detail pages with specifications, and mobile-first responsive design.',
    technologies: ['SvelteKit 2.0', 'TypeScript 5', 'Tailwind CSS 3', 'Azure Static Web Apps'],
    githubUrl: 'https://github.com/davidagustin/azure-static-practice',
    liveUrl: 'https://ashy-sky-078d0441e.2.azurestaticapps.net',
    category: 'E-commerce',
    features: [
      '8 Product Categories',
      'Shopping Cart',
      'Search & Filter',
      'Mobile-First Design',
    ],
    readmeHighlights: {
      overview: 'A comprehensive e-commerce platform for computer hardware offering a premium shopping experience with 8+ product categories, advanced search, and cart management. Deployed on Azure Static Web Apps with global CDN.',
      highlights: [
        '8+ categories: Processors, Graphics Cards, Storage, Memory, Motherboards, Cooling, Power Supplies, and Cases',
        'Advanced search with real-time filtering by name, category, and description',
        'Shopping cart with quantity management and real-time price calculation',
        'Star rating system for customer reviews',
        'Azure Static Web Apps deployment with HTTPS and CI/CD on git push',
        'SEO optimized with meta tags and structured data',
        'PWA-ready with Progressive Web App capabilities',
      ],
      techDetails: 'SvelteKit 2.0 with TypeScript 5.0, Tailwind CSS 3.3.5, and Lucide Svelte icons. Static site generation with full TypeScript implementation.',
    },
  },
  {
    id: 5,
    title: '20 Newsgroups ML Dashboard',
    description:
      'Complete machine learning pipeline and analysis dashboard for the 20 Newsgroups text classification dataset. Features 10+ ML algorithms including Logistic Regression, SVM, XGBoost, and LightGBM with advanced hyperparameter tuning via Grid Search CV and interactive visualizations.',
    technologies: ['Next.js 15', 'React 18', 'TypeScript', 'Material-UI', 'Python', 'Scikit-learn', 'XGBoost', 'LightGBM'],
    githubUrl: 'https://github.com/davidagustin/machine-learning-project',
    liveUrl: 'https://machine-learning-project-theta.vercel.app',
    category: 'AI/ML',
    features: [
      '10+ ML Algorithms',
      'Grid Search CV Tuning',
      'Interactive Visualizations',
      'Static Data Architecture',
    ],
    readmeHighlights: {
      overview: 'A comprehensive ML analysis dashboard for the 20 Newsgroups dataset (18,846 documents across 20 categories), featuring advanced hyperparameter tuning with Grid Search CV and multi-algorithm comparison with interactive visualizations.',
      highlights: [
        'Top performer: LightGBM at 92.1% accuracy with 2.3s training time',
        'Dataset: 18,846 documents, 4,876 unique words, TF-IDF with unigrams/bigrams',
        'Pre-computed results for instant loading with no computational delays',
        'Interactive confusion matrices and multi-class ROC curves with AUC scores',
        'Performance: First Contentful Paint <1.5s, bundle size <500KB gzipped',
        'Hyperparameter optimization with cross-validation results visualization',
      ],
      techDetails: 'Next.js 14 (App Router) with React 18, TypeScript, Material-UI 5, and Chart.js. Python 3.8+ backend with Scikit-learn, XGBoost/LightGBM. Static data served via API routes for optimal performance.',
    },
  },
  {
    id: 6,
    title: 'MLI Learning Quiz',
    description:
      'Interactive learning and mnemonic quiz application for MLI precourse material. 5 quiz categories covering NumPy, Pandas, Prompt Engineering, ML Fundamentals, and Spatial Data with randomized questions, progress analytics, and performance badges.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript', 'Tailwind CSS 4', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/ai-quiz-questions',
    liveUrl: 'https://ai-quiz-questions.vercel.app',
    category: 'Education',
    features: [
      '5 Quiz Categories',
      'Smart Mnemonics',
      'Performance Badges',
      'Study Recommendations',
    ],
    readmeHighlights: {
      overview: 'Interactive learning and mnemonic quiz application for MLI precourse material, helping users master complex concepts through quizzes and powerful memory aids with 5 categories and comprehensive progress tracking.',
      highlights: [
        'Smart mnemonics: SHAPE, DOT, MEAN for NumPy; CRUD, GROUP for Pandas; CLEAR, SMART for Prompt Engineering',
        'Study Mode with confidence-based ratings and localStorage persistence',
        'Progress tracking with quiz history, performance analytics, visual charts, and achievements',
        'Dedicated mnemonics reference page with explanations and study tips',
        'Real-time progress bars, question navigation, and animated results',
        'No environment variables required - progress stored in browser localStorage',
      ],
      techDetails: 'Next.js 15 with React 19, TypeScript, and Tailwind CSS 4 with custom gradients/animations. React hooks with localStorage for state persistence.',
    },
  },
  {
    id: 7,
    title: 'Hope Foundation',
    description:
      'Responsive charity website built with Gatsby. Features donation systems, volunteer portals, event management, and program showcases with excellent performance and SEO.',
    technologies: ['Gatsby', 'React', 'Digital Ocean', 'SEO'],
    githubUrl: 'https://github.com/hope-foundation/charity-website',
    liveUrl: 'https://sample-gatsby-6fe3p.ondigitalocean.app/',
    category: 'Non-Profit',
    features: [
      'Donation System',
      'Volunteer Portal',
      'Event Management',
      'Program Showcase',
    ],
    readmeHighlights: {
      overview: 'Responsive charity website built with Gatsby featuring donation systems, volunteer portals, event management, and program showcases with excellent Lighthouse performance scores.',
      highlights: [
        'Gatsby static site generator for blazing-fast performance',
        'Donation system with multiple payment options',
        'Volunteer portal for sign-ups and management',
        'Event management with calendar and registration',
        'Program showcase pages with detailed descriptions',
        'Deployed on Digital Ocean App Platform',
      ],
      techDetails: 'Gatsby with React, GraphQL data layer, and Digital Ocean App Platform deployment. SEO-optimized with structured data and social meta tags.',
    },
  },
  {
    id: 8,
    title: 'Historigal',
    description:
      'Search engine for 37,860+ historical events from 300 B.C. to 2012 with a Google-like interface. Features real-time autocomplete with 300ms debouncing, smart filtering across descriptions and dates, URL-based routing for bookmarkable searches, and seamless browser navigation.',
    technologies: ['Next.js 15', 'TypeScript 5', 'Tailwind CSS', 'React Hooks', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/historigal-vercel',
    liveUrl: 'https://historigal-vercel.vercel.app',
    category: 'Data/Research',
    features: [
      '37,860+ Events',
      'Google-like Autocomplete',
      'URL-Based Search Routing',
      'Debounced Filtering',
    ],
    readmeHighlights: {
      overview: 'A modern historical events search application with a Google-like interface, featuring 37,860+ events from 300 B.C. to 2012 with advanced autocomplete, intelligent citation parsing, and URL-based routing.',
      highlights: [
        '37,860+ historical events spanning from 300 B.C. to 2012',
        'Google-like autocomplete with debounced search (300ms) and instant results',
        'URL routing for search queries with browser back/forward navigation support',
        'Intelligent citation parsing: supports cite web/news/book/journal/magazine formats',
        'HTML entity decoding for special characters',
        'Smart pagination: client-side with 20 items per page and responsive controls',
      ],
      techDetails: 'Next.js 15 with TypeScript 5.0, Tailwind CSS 3.3, and React Hooks. Next.js API Routes for serverless endpoints with local db.json for data storage.',
    },
  },
  {
    id: 9,
    title: 'AI Learning Hub',
    description:
      'Comprehensive application for learning AI and machine learning concepts through interactive mnemonics and character-based learning. Covers Core ML, Deep Learning, Computer Vision, NLP, Advanced AI, MLOps, and Ethics across 30+ sections with hundreds of quiz questions.',
    technologies: ['Next.js 15', 'TypeScript', 'Tailwind CSS', 'React Hooks', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/comprehensive-ai-learning-app',
    liveUrl: 'https://comprehensive-ai-learning-app.vercel.app',
    category: 'Education',
    features: [
      '7 Learning Paths',
      'Hundreds of Quizzes',
      'Character-based Learning',
      '30+ AI/ML Topics',
    ],
    readmeHighlights: {
      overview: 'A comprehensive Next.js application for learning AI and machine learning through interactive character-based mnemonics, memory frameworks, and quizzes covering 30+ sections across all major AI/ML topics.',
      highlights: [
        'Character-based learning with 8 personified ML algorithms (Simple Sam, Robust Robert, eXtreme Xavier, etc.)',
        'Memory frameworks: RCCA, LRXS, LRSN for categorizing and remembering algorithms',
        '1000+ quiz questions across 10+ categories with customizable question count',
        'Complete coverage: Data Splitting, Epochs, Feature Selection, NLP, Deep Learning, and more',
        'Glass morphism design with dark mode support and smooth animations',
        '199 kB First Load JS bundle with 5 static pages on Vercel',
      ],
      techDetails: 'Next.js 15.4.4 with App Router, TypeScript 5, and Tailwind CSS 3.4.0. React Hooks for state management with full accessibility and keyboard navigation.',
    },
  },
  {
    id: 10,
    title: 'A Very Nice Grocery List',
    description:
      'Modern, responsive grocery list application with duplicate prevention, auto-scroll, and a beautiful dark theme with smooth animations.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript 5', 'Tailwind CSS', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/a-very-nice-grocery-list-vercel',
    liveUrl: 'https://a-very-nice-grocery-list-vercel.vercel.app',
    category: 'Productivity',
    features: [
      'Add & Remove Items',
      'Duplicate Prevention',
      'Auto-scroll',
      'Dark Theme',
    ],
    readmeHighlights: {
      overview: 'A modern, responsive grocery list application with a clean, intuitive dark-themed interface featuring smart validation to prevent duplicates, one-click item removal, and auto-scroll.',
      highlights: [
        'Smart validation prevents adding duplicate items',
        'One-click item deletion with no confirmation needed',
        'Auto-scroll automatically scrolls to show newly added items',
        'Dark color scheme: teal green background, maroon accents, white text',
        'Responsive: desktop side-by-side, tablet stacked, mobile compact layouts',
        'Adapted from original A-Very-Nice-Grocery-List repository',
      ],
      techDetails: 'Next.js 15 (App Router) with React 19 and TypeScript 5. Component structure: App, Input, List, ListItems, ClientOnly wrapper. Custom CSS3 styling.',
    },
  },
  {
    id: 11,
    title: 'Luxury Realty',
    description:
      'Comprehensive real estate website with luxury property listings. Features advanced search by location, type, price and status, property detail pages with image galleries, filtering across houses, apartments, condos, townhouses, and land, and professional UI with smooth animations.',
    technologies: ['Angular 17', 'TypeScript', 'CSS3', 'Surge.sh'],
    githubUrl: 'https://github.com/davidagustin/surge-practice',
    liveUrl: 'https://my-surge-practice-site.surge.sh',
    category: 'Real Estate',
    features: [
      'Advanced Property Search',
      '5 Property Types',
      'Image Galleries',
      'Status Filtering',
    ],
    readmeHighlights: {
      overview: 'Comprehensive real estate website built with Angular 17 standalone components, showcasing luxury properties with advanced multi-criteria search and responsive design optimized for all devices.',
      highlights: [
        'Angular 17 standalone components with TypeScript for type-safe development',
        'Advanced filtering by location, price range, property type, bedrooms/bathrooms, and status',
        'Bundle size ~69KB gzipped with Lighthouse score 95+ across all metrics',
        'Comprehensive property data model with coordinates, agent info, and 6 images per listing',
        'Deployed on Surge.sh with CDN distribution for fast global loading (< 2 seconds)',
      ],
      techDetails: 'Angular 17 standalone components, TypeScript, production deployment on Surge.sh with optimized bundles, minified code, and CDN distribution.',
    },
  },
  {
    id: 12,
    title: 'Elegant Furniture Store',
    description:
      'Modern furniture store built with Vue.js 3 Composition API and Tailwind CSS. Features a product catalog with category browsing and search, shopping cart with real-time updates via Pinia, detailed product pages with specifications, and client-side routing with Vue Router.',
    technologies: ['Vue.js 3', 'Vite 4', 'Tailwind CSS', 'Pinia', 'Vue Router', 'Render'],
    githubUrl: 'https://github.com/davidagustin/render-practice',
    liveUrl: 'https://render-practice-or72.onrender.com',
    category: 'E-commerce',
    features: [
      'Product Catalog',
      'Shopping Cart',
      'Responsive Design',
      'State Management',
    ],
    readmeHighlights: {
      overview: 'Modern furniture e-commerce store built with Vue.js 3 Composition API and deployed on Render. Features shopping cart with real-time updates, product filtering, and Pinia state management.',
      highlights: [
        'Vue.js 3 Composition API with Vite 4 for lightning-fast builds',
        'Pinia 2 store managing cart state and product catalog with real-time updates',
        'Search, category filtering, price range filtering, and sorting by name/price',
        'Responsive product grid with sidebar cart and real-time total calculation',
        'Complete product data model with dimensions, colors, stock status, and features',
      ],
      techDetails: 'Vue.js 3 Composition API, Vite 4, Tailwind CSS 3, Vue Router 4 for routing, and Pinia 2 for centralized state management. Deployed on Render.',
    },
  },
  {
    id: 13,
    title: 'Law & Associates',
    description:
      'Professional law firm website with dark-themed modern design. Features 6 practice areas (Business, Real Estate, Family, Civil Litigation, Estate Planning, Criminal Defense), attorney team profiles, legal resource forms and articles, and consultation booking.',
    technologies: ['Astro.js', 'Tailwind CSS', 'TypeScript', 'Netlify'],
    githubUrl: 'https://github.com/davidagustin/netlify-practice',
    liveUrl: 'https://netlify-practice-one.netlify.app',
    category: 'Professional Services',
    features: [
      '6 Practice Areas',
      'Team Profiles',
      'Legal Resources',
      'Consultation Booking',
    ],
    readmeHighlights: {
      overview: 'Professional law firm website built with Astro.js and Tailwind CSS, deployed on Netlify with comprehensive practice area coverage, attorney profiles, and legal resources.',
      highlights: [
        'Astro.js static site generator for fast, SEO-friendly pages with server-side rendering',
        '6 practice areas: Business, Real Estate, Family, Civil Litigation, Estate Planning, Criminal Defense',
        'Professional dark theme with responsive mobile-first design',
        'Automatic deployments from Git repository on Netlify',
        'Legal resources section with forms, articles, and educational content',
      ],
      techDetails: 'Astro static site generator, Tailwind CSS utility-first framework, deployed on Netlify with automatic Git-based deployments and SSR for SEO.',
    },
  },
  {
    id: 14,
    title: 'FoodRater',
    description:
      'Restaurant rating application inspired by Yelp, built with Angular 17 and Firebase. Features restaurant discovery with cuisine, location and price filtering, 5-star rating system with half-star support and detailed reviews, Firebase authentication, and real-time data updates.',
    technologies: ['Angular 17', 'Firebase', 'TypeScript', 'Tailwind CSS', 'Material Design', 'RxJS'],
    githubUrl: 'https://github.com/davidagustin/firebase-practice',
    liveUrl: 'https://firebase-practice--fir-practice-54eb3.us-central1.hosted.app/',
    category: 'Food & Rating',
    features: [
      'Restaurant Discovery',
      '5-Star + Half-Star Ratings',
      'Firebase Authentication',
      'Real-time Reviews',
    ],
    readmeHighlights: {
      overview: 'Restaurant rating application built with Angular 17 and Firebase, featuring secure authentication, real-time Firestore synchronization, and a 5-star rating system with comprehensive reviews.',
      highlights: [
        'Firebase: Firestore for real-time data, Authentication for secure login, Hosting for deployment',
        '10 sample restaurants with realistic data across multiple cuisines and price ranges',
        'Comprehensive security with Firestore rules, XSS protection, and role-based permissions',
        'Advanced filtering by cuisine, location, price range with real-time debounced search',
        'Automated scripts for Firebase configuration, database seeding, and security deployment',
        'Material Design components with loading states and error handling',
      ],
      techDetails: 'Angular 17 standalone components, Firebase (Firestore, Auth, Hosting), RxJS Observables for state, Tailwind CSS with Angular Material.',
    },
  },
  {
    id: 15,
    title: 'Sudoku Game',
    description:
      'Interactive Sudoku game with three difficulty levels. Click cells and type numbers with real-time validation, visual feedback through highlighted cells and error indicators, auto-solve functionality, and modern gradient UI. Supports keyboard controls including backspace to clear.',
    technologies: ['React 18', 'Express.js', 'Node.js', 'Webpack 5', 'Heroku'],
    githubUrl: 'https://github.com/davidagustin/heroku-practice',
    liveUrl: 'https://heroku-sudoku-49243446e4d3.herokuapp.com/',
    category: 'Gaming',
    features: [
      'Three Difficulty Levels',
      'Real-time Validation',
      'Interactive Gameplay',
      'Modern UI',
    ],
    readmeHighlights: {
      overview: 'Interactive Sudoku game with three difficulty levels featuring real-time validation against solutions, visual feedback, and auto-solve functionality with keyboard controls.',
      highlights: [
        'Pre-defined puzzles with correct solutions for validation',
        'Real-time validation with immediate feedback on correct/incorrect moves',
        'Visual feedback: highlighted cells, error indicators, completion celebration',
        'Full keyboard: type 1-9 to fill, Backspace/Delete/0 to clear',
        'Modern gradient UI with smooth animations',
        'Express server serving React static build on Heroku',
      ],
      techDetails: 'React 18 frontend, Express.js backend server, Webpack 5 bundling, deployed on Heroku with production build at port 5006.',
    },
  },
  {
    id: 16,
    title: 'Rules of ML',
    description:
      "Interactive app to memorize Google's 43 Rules of Machine Learning Engineering. Three study modes: Browse with category filtering and search, Study with sequential progress tracking, and Flashcard for active recall. Covers all ML phases from first pipeline to complex models.",
    technologies: ['Next.js 15', 'TypeScript', 'Tailwind CSS', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/rules-of-machine-learning',
    liveUrl: 'https://rules-of-machine-learning.vercel.app/',
    category: 'Education',
    features: [
      '43 ML Rules',
      'Three Study Modes',
      '12 Rule Categories',
      'Progress Tracking',
    ],
    readmeHighlights: {
      overview: 'Interactive memorization app for Google\'s 43 Rules of Machine Learning Engineering with three learning modes, progress tracking, dark mode, and category-based filtering across 4 ML phases.',
      highlights: [
        'Complete database of all 43 rules organized into 4 phases (Pre-ML, Phase I, II, III)',
        '12 category filters including Feature Engineering, Monitoring/Debugging, Model Evaluation',
        'Three modes: Browse (expandable cards), Study (sequential), Flashcard (randomized shuffle)',
        'Progress tracking with visual progress bars and card navigation',
        'Search across rule titles and descriptions with phase indicator badges',
        'Dark mode with automatic theme switching',
      ],
      techDetails: 'Next.js 15 App Router for SSR, TypeScript, Tailwind CSS with dark mode, deployed on Vercel with static site generation.',
    },
  },
  {
    id: 17,
    title: 'Sign Language Learning',
    description:
      'Learn to spell your name in ASL with real-time AI-powered hand tracking. Uses MediaPipe Hands API for computer vision with precise finger state detection (open/closed), color-coded hand landmarks, and hand stability monitoring for better accuracy.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript 5', 'Tailwind CSS 3', 'MediaPipe Hands', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/spell-your-name',
    liveUrl: 'https://spell-your-name.vercel.app/',
    category: 'AI/ML',
    features: [
      'Real-time Hand Tracking',
      'AI Gesture Recognition',
      'Personalized Learning',
      'ASL Alphabet Guide',
    ],
    readmeHighlights: {
      overview: 'Real-time AI-powered sign language learning app using MediaPipe Hands for computer vision. Teaches users to spell their name in ASL with personalized, letter-by-letter instruction and instant AI feedback.',
      highlights: [
        'MediaPipe Hands API with 21-point hand tracking and 3D coordinate analysis',
        'Advanced finger state detection with circle-based recognition and stability validation',
        'Complete ASL alphabet reference guide with visual hand sign images for all 26 letters',
        'Real-time visual feedback with hand positioning guides, progress bars, and achievements',
        'Full-screen mode with YouTube-style button for immersive learning',
        'No external environment variables required - works out of the box',
      ],
      techDetails: 'Next.js 15.4.5 (App Router) with React 19, TypeScript 5, Tailwind CSS, MediaPipe Hands for computer vision, and custom ASL pattern matching algorithms.',
    },
  },
  {
    id: 18,
    title: 'Algomations',
    description:
      'Interactive visualization platform for mastering coding interview algorithms. 301 problems across 19 pattern categories with step-by-step code execution, real-time data structure animations, multi-language code display, and an AI tutor powered by WebLLM.',
    technologies: ['Next.js 16', 'React 19', 'TypeScript', 'Tailwind CSS 4', 'Framer Motion', 'WebLLM'],
    githubUrl: 'https://github.com/davidagustin/algorithm-visualizations',
    liveUrl: 'https://algomations.vercel.app',
    category: 'Education',
    features: [
      '301 Visualizations',
      '19 Categories',
      'AI Tutor',
      'Interview Filter',
    ],
    readmeHighlights: {
      overview: 'Interactive visualization platform for mastering coding interview algorithms with 301 problems across 19 pattern categories.',
      highlights: [
        '301 algorithm visualizations covering Two Pointers, Hash Maps, Trees, DP, Graphs, and more',
        'Step-by-step code execution with variable tracking and data structure animations',
        'Multi-language code display: Python, JavaScript, Java, and Pseudocode',
        'Interview recommended filter curating classic problems across all categories',
        'AI tutor powered by on-device WebLLM — no API keys needed',
        'Dark/light mode, responsive design, and keyboard navigation support',
      ],
      techDetails: 'Next.js 16 App Router, React 19, TypeScript, Tailwind CSS 4, Framer Motion for animations, WebLLM for AI tutor.',
    },
  },
  {
    id: 19,
    title: 'UI Patterns React',
    description:
      'Production-ready collection of 90+ modern React UI patterns and components. Enterprise-grade codebase demonstrating senior-level React skills with 100% TypeScript coverage, WCAG 2.1 AA accessibility compliance, full keyboard navigation, and scalable architecture.',
    technologies: ['Next.js 15', 'React 19', 'TypeScript 5.6', 'Tailwind CSS 3.4', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/ui-patterns-react',
    liveUrl: 'https://ui-patterns-react.vercel.app/',
    category: 'Component Library',
    features: [
      '90+ UI Patterns',
      '100% TypeScript',
      'WCAG 2.1 AA',
      'Dark Mode',
    ],
    readmeHighlights: {
      overview: 'Production-ready collection of 90+ modern React UI patterns and components with enterprise-grade architecture, 100% TypeScript coverage, and WCAG 2.1 AA accessibility compliance.',
      highlights: [
        '90 production-ready components with professional code quality',
        '50,000+ lines of clean code with zero TypeScript errors',
        '100% responsive design with mobile-first approach',
        'Interactive event calendar with drag-and-drop, advanced data tables with sorting/filtering',
        '9/10 production readiness score with full accessibility compliance',
        'Lighthouse: Performance 95+, Accessibility 100, Best Practices 100, SEO 100',
      ],
      techDetails: 'Next.js 15 App Router with Server Components, React 19, TypeScript 5.6 strict, Tailwind CSS 3.4. Optimized for Core Web Vitals (LCP < 2.5s, FID < 100ms, CLS < 0.1).',
    },
  },
  {
    id: 20,
    title: 'React Interview Sandbox',
    description:
      'Professional code sandbox designed for coding interviews. Features built-in timer persisting through refreshes, live preview with hot reload, resizable panels, Prism.js syntax highlighting, dual styling (BEM + Tailwind), and 56+ tests covering unit, integration, accessibility, and performance.',
    technologies: ['React 18', 'TypeScript', 'BEM CSS', 'Tailwind CSS', 'Prism.js', 'Jest'],
    githubUrl: 'https://github.com/davidagustin/react-interview-sandbox',
    category: 'Development Tools',
    features: [
      'Interview Timer',
      'Live Code Preview',
      '56+ Test Suite',
      'Syntax Highlighting',
    ],
    readmeHighlights: {
      overview: 'Professional React TypeScript code sandbox designed for coding interviews with built-in timer, live preview, and comprehensive testing suite featuring glassmorphism UI and BEM methodology.',
      highlights: [
        'Interview timer with localStorage persistence through page refreshes',
        'Live code preview with hot reload and resizable panels',
        '56+ comprehensive tests: unit, integration, accessibility, and performance',
        'Dual styling: BEM CSS methodology and Tailwind CSS utility classes',
        'Professional syntax highlighting with Prism.js TypeScript/JSX support',
        'Diverse querying strategies demonstrating multiple testing approaches',
        'Clean slate browser preview with no external CSS for interviews',
      ],
      techDetails: 'React 18 with TypeScript and Create React App. Jest and React Testing Library with multiple querying strategies. BEM CSS architecture with optional Tailwind integration.',
    },
  },
  {
    id: 21,
    title: 'PIIxelate',
    description:
      'AI-powered privacy protection tool that detects and pixelates PII in images. Uses multi-layer detection for credit cards, driver\'s licenses, phone numbers, emails, addresses, medical records, and barcodes. Browser-based local processing with optional AI services for 95-99% accuracy.',
    technologies: ['Next.js 15', 'TypeScript', 'Tailwind CSS', 'Tesseract.js', 'OpenAI', 'Anthropic'],
    githubUrl: 'https://github.com/davidagustin/piixelate',
    liveUrl: 'https://piixelate.vercel.app',
    category: 'AI/ML',
    features: [
      '7-Layer Detection',
      'Browser-Based Processing',
      'Privacy-First Design',
      'Multi-API Support',
    ],
    readmeHighlights: {
      overview: 'AI-powered privacy protection tool using multi-layer PII detection and pixelation. 7-layer architecture combining computer vision, OCR, pattern matching, specialized detection, LLM verification, and ensemble analysis for 95-99% accuracy.',
      highlights: [
        '7-layer detection: computer vision, OCR, pattern matching, specialized detection, LLM verification, ensemble analysis, obscuring',
        'Detects 30+ PII types: credit cards with Luhn validation, government IDs, medical records, IP addresses, crypto wallets',
        '15+ credit card formats with context-aware validation',
        'Detection accuracy: 85-95% pattern matching, 90-98% LLM verification, 95-99% combined',
        'Performance: small images (<1MB) in 1-3s, large images (5-10MB) in 8-15s',
        'Real-time statistics dashboard with progress tracking and advanced configuration',
      ],
      techDetails: 'Next.js 15.4.6 with React 19 and TypeScript 5. Tesseract.js for OCR, TensorFlow.js for CV, Workers AI (Llama 3.1 8B) for LLM verification. Enhanced Sobel edge detection with contour analysis.',
    },
  },
  {
    id: 22,
    title: 'Classic Snake Game',
    description:
      'Modern implementation of the classic Snake game with 60fps smooth gameplay, zero input lag, and custom animations. Features mobile-first responsive design with touch controls, high score persistence, cross-platform compatibility, and accessibility compliance.',
    technologies: ['React 19', 'TypeScript 5', 'Tailwind CSS 4', 'Vite 6', 'Framer Motion'],
    githubUrl: 'https://github.com/davidagustin/classic-snake-game',
    liveUrl: 'https://classic-snake-game-phi.vercel.app/',
    category: 'Gaming',
    features: [
      '60fps Gameplay',
      'Responsive Controls',
      'High Score Tracking',
      'Mobile-First',
    ],
    readmeHighlights: {
      overview: 'Modern responsive implementation of the classic Snake game with 60fps smooth gameplay, zero input lag, and cross-platform compatibility featuring touch-friendly mobile interface.',
      highlights: [
        '60fps smooth animation with optimized rendering',
        'Persistent high scores using localStorage',
        'Mobile-first design with touch controls and responsive breakpoints',
        'Custom hooks for reusable game logic, keyboard controls, auto-pause, and mobile detection',
        'Modular components: GameBoard, GameHeader, GameControls, GameOverlay',
        'Error boundaries for graceful error handling',
        'Dual keyboard controls: arrow keys and WASD',
        'JetBrains Mono font with hardware-accelerated animations',
      ],
      techDetails: 'React 19, TypeScript 5.7.2, Tailwind CSS 4.1, Vite 6.3.5. Radix UI primitives for accessibility, Phosphor Icons. Optimized game loop with useCallback and efficient state management.',
    },
  },
  {
    id: 23,
    title: 'Backend Engineer Detective',
    description:
      'Interactive detective game with 121 real-world backend incident scenarios across 11 categories. Investigate clues progressively through error logs, metrics, code snippets, config files, and engineer testimonies. Features an AI mentor (Detective Claude) using Socratic guidance and automated scoring.',
    technologies: ['Cloudflare Workers', 'TypeScript', 'Hono', 'Llama 3.1'],
    githubUrl: 'https://github.com/davidagustin/backend-engineer-detective',
    liveUrl: 'https://backend-engineer-detective.app-production.workers.dev/',
    category: 'Education',
    features: [
      '121 Detective Cases',
      '11 Incident Categories',
      'AI Mentor Guidance',
      'Automated Scoring',
    ],
    readmeHighlights: {
      overview: 'Interactive detective game with 121 production incident scenarios teaching real-world backend debugging. AI-powered mentor using Llama 3.1 8B for Socratic guidance with two-phase LLM-evaluated diagnosis.',
      highlights: [
        '121 cases across 11 categories: Core Backend, AWS, Databases, Message Queues, Kubernetes, Auth, Monitoring, Runtimes, Load Balancing, Resilience, DevOps',
        'Progressive clue reveal: metrics, logs, code snippets, config files, engineer testimonies',
        'AI Detective Claude mentor provides Socratic questions without revealing answers',
        'Two-phase diagnosis scored 0-100 by LLM',
        'Difficulty levels: Junior, Mid, Senior, Principal',
        'SSE streaming for real-time chat on Cloudflare Workers',
        'Vanilla HTML/JS/CSS with no framework for zero-config deployment',
      ],
      techDetails: 'Cloudflare Workers with TypeScript, Workers AI (Llama 3.1 8B), SSE streaming. Vanilla JavaScript frontend with detective noir theme, Lucide icons, and Prism.js syntax highlighting.',
    },
  },
  {
    id: 24,
    title: 'Coding Tricks Practice',
    description:
      'LeetCode-style practice platform with 155+ JavaScript and TypeScript challenges across 19 categories. Features Monaco Editor for a professional coding experience, real-time validation with IntelliSense, sandboxed execution with test validation, and progress tracking with difficulty filtering.',
    technologies: ['Next.js 16', 'React 19', 'TypeScript 5.9', 'Tailwind CSS 4', 'Monaco Editor', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/coding-tricks-practice',
    liveUrl: 'https://coding-tricks-practice.vercel.app',
    category: 'Education',
    features: [
      '155+ Challenges',
      '19 Categories',
      'Monaco Editor',
      'Sandboxed Execution',
    ],
    readmeHighlights: {
      overview: 'Production-grade LeetCode-style learning platform with 155+ JavaScript/TypeScript challenges across 19 categories featuring Monaco Editor with sandboxed execution and comprehensive progress tracking.',
      highlights: [
        '155+ problems: JS basics, arrays, async/promises, TypeScript, functional programming, design patterns, algorithms',
        '140K+ lines of code with 40+ test files',
        'Monaco Editor: syntax highlighting, IntelliSense, auto-completion, multiple themes',
        'Sandboxed execution with detailed error reporting, stack traces, and performance timing',
        'Progress: persistent localStorage, streak tracking, real-time dashboard',
        'Security: sandboxed execution, DOMPurify sanitization, timeout protection, XSS prevention',
        'WCAG 2.1 AA accessibility compliance',
      ],
      techDetails: 'Next.js 16 App Router with React 19, TypeScript 5.9, Tailwind CSS 4.0. Monaco Editor, Jest/React Testing Library, Biome linter. Vercel-optimized deployment.',
    },
  },
  {
    id: 25,
    title: 'React 30',
    description:
      'Modern React reimagining of Wes Bos\'s JavaScript30 course. 30 vanilla JS projects rebuilt as type-safe React components covering DOM manipulation, canvas drawing, media handling, and browser APIs. Includes drum kit, analog clock, webcam integration, speech recognition, and geolocation.',
    technologies: ['Next.js 16', 'React 19', 'TypeScript 5', 'Tailwind CSS 4', 'Vercel'],
    githubUrl: 'https://github.com/davidagustin/react-30',
    liveUrl: 'https://react-30-nu.vercel.app',
    category: 'Education',
    features: [
      '30 React Projects',
      'Spec-Driven Development',
      'Browser API Integration',
      'Canvas & Media',
    ],
    readmeHighlights: {
      overview: 'Modern React implementation of Wes Bos\'s JavaScript30 course, transforming 30 vanilla JS projects into type-safe React components with spec-driven development and automated CI/CD.',
      highlights: [
        '30 interactive projects: drum kit, analog clock, video player, webcam, speech recognition, geolocation, canvas drawing',
        'Spec-Driven Development (SDD) with structured specifications',
        'Full TypeScript strict mode with comprehensive type definitions',
        'Web APIs: DOM, Canvas, WebRTC, Geolocation, Speech Recognition/Synthesis, LocalStorage, Media APIs',
        'Automated CI/CD pipeline with linting, type checking, build verification, staged deployments',
        'Performance: code splitting, lazy loading, Next.js Image optimization, Server Components',
      ],
      techDetails: 'Next.js 16 App Router with Server Components, React 19, TypeScript 5, Tailwind CSS 4. Modular component architecture with Biome linter and Vercel deployment.',
    },
  },
  {
    id: 26,
    title: 'TypeCode',
    description:
      'Precision typing practice application for programmers. Type syntactically correct code across 49 lessons spanning 46 languages including React, Angular, and Vue with real-time WPM and accuracy tracking. Features syntax highlighting with color reveal, a pure reducer-based typing engine, language-aware comment auto-completion with inline detection, custom code upload with drag-and-drop, and edge deployment on Cloudflare Workers.',
    technologies: ['React 19', 'TypeScript 5.9', 'React Router 7', 'Tailwind CSS 4', 'Vite 6', 'Cloudflare Workers'],
    githubUrl: 'https://github.com/davidagustin/typing-app',
    liveUrl: 'https://typing-app.app-production.workers.dev/',
    category: 'Education',
    features: [
      '49 Lessons, 46 Languages',
      'Syntax Highlighting',
      'Real-time WPM Tracking',
      'Custom Code Upload',
      'Smart Auto-Completion',
    ],
    readmeHighlights: {
      overview: 'A precision typing practice app for programmers featuring 49 built-in lessons across 46 languages including React, Angular, Vue, and more, with syntax highlighting, real-time keystroke metrics, custom code upload, and language-aware auto-completion. Zero external UI or state libraries with only 3 production dependencies.',
      highlights: [
        '49 built-in lessons across 46 languages including React, Angular, Vue, Rust, Go, Haskell, and more',
        'Syntax highlighting with One Dark theme and word-completion color reveal',
        'Pure reducer-based typing engine with deterministic state transitions and character-by-character verification',
        'Language-aware comment detection with inline comment support (//, #, --, /* */) and space-after-marker heuristic',
        'Custom code upload with drag-and-drop, auto-detection for 20+ file extensions, and localStorage persistence',
        'Real-time stats: WPM, accuracy rate, correct/incorrect keystrokes with performance rating system',
        'Edge deployment on Cloudflare Workers for global low-latency with SSR via React Router 7',
      ],
      techDetails: 'React 19 with React Router 7 full-stack framework, TypeScript 5.9, Tailwind CSS 4, Vite 6. Cloudflare Workers edge deployment with SSR. Zero external UI/state libraries — only 3 production dependencies.',
    },
  },
  {
    id: 27,
    title: 'Game Room',
    description:
      'Real-time multiplayer game platform with Tic-Tac-Toe and Chess. Turborepo monorepo with an Expo React Native client, Fastify 5 server, and shared game logic package. Features Socket.IO rooms for sub-100ms gameplay, dual-storage pattern (Redis ephemeral + PostgreSQL persistent), JWT auth with refresh token rotation, AI opponents with minimax/alpha-beta pruning, ELO rating system, and full CI/CD pipeline deploying via Docker to AWS.',
    technologies: ['Expo 52', 'React Native', 'Fastify 5', 'Socket.IO', 'PostgreSQL', 'Redis', 'Terraform', 'Docker'],
    githubUrl: 'https://github.com/davidagustin/tic-tac-toe',
    liveUrl: 'https://game-practice-aws.com/',
    category: 'Gaming',
    features: [
      'Real-time Multiplayer',
      'Chess + Tic-Tac-Toe',
      'AI Opponents',
      'AWS Infrastructure',
    ],
    readmeHighlights: {
      overview: 'Real-time multiplayer game platform built as a Turborepo monorepo with an Expo mobile client, Fastify server, and shared game logic kernel. Supports Tic-Tac-Toe and Chess with online multiplayer, spectator mode, lobby chat, AI opponents, ELO ratings, and full AWS infrastructure-as-code.',
      highlights: [
        'Polymorphic game engine registry: tttEngine and chessEngine implement a shared GameEngine interface, making new games a single-file addition',
        'Dual-storage pattern: Redis for ephemeral state (rooms, presence, chat with 2-hour TTL) and PostgreSQL for persistent data (accounts, completed games, stats)',
        'Typed Socket.IO events via shared ClientToServerEvents/ServerToClientEvents interfaces — payload mismatches caught at compile time',
        'AI opponents: minimax (perfect play) for Tic-Tac-Toe, alpha-beta pruning with piece-square evaluation tables for Chess at 3 difficulty levels',
        'JWT auth with refresh token rotation, Google OAuth, guest mode, CSRF protection, and per-room chat rate limiting via Redis sliding window',
        'Terraform IaC (EC2, SSM, security groups), multi-stage Docker builds, and CI/CD with dynamic SSH allow-listing in GitHub Actions',
      ],
      techDetails: 'Turborepo 2.0 monorepo with Expo 52 (React Native 0.76), Fastify 5, Socket.IO 4.8 + Redis adapter, Prisma 5.20 (PostgreSQL 16), Zustand 4.5, chess.js, NativeWind 4.1. Terraform for AWS, Nginx reverse proxy with SSL, and GitHub Actions CI/CD.',
    },
  },
  {
    id: 28,
    title: 'Pickleball Research',
    description:
      'The most comprehensive pickleball research platform — 51 pages, 1,014 paddles, 266 pro athletes, 130+ drills, and a 46,458-line interactive magazine. Built with Next.js 16, React 19, TypeScript, Tailwind CSS v4, and 15+ cloud services.',
    technologies: ['Next.js 16', 'React 19', 'TypeScript', 'Tailwind CSS v4', 'Supabase', 'Pinecone', 'Typesense', 'tRPC', 'Upstash Redis', 'BetterAuth', 'PostHog', 'Sentry', 'Axiom', 'Trigger.dev', 'Cloudinary', 'Vitest', 'Playwright', 'Storybook', 'Biome'],
    githubUrl: '',
    liveUrl: 'https://pickleball-research.com',
    category: 'Research',
    features: [
      '51 Page Routes',
      '1,014 Paddle Database',
      '266 Pro Athletes',
      '130+ Drills Library',
      '46,458-Line Magazine',
      '798 Automated Tests',
      '15+ Cloud Services',
    ],
    readmeHighlights: {
      overview: 'The most comprehensive pickleball research platform — 51 pages, 1,014 paddles from 92 brands, 266 pro athletes, 130+ drills, a 46,458-line interactive magazine, and AI-powered tools built with Next.js 16, React 19, TypeScript, Tailwind CSS v4, and 15+ cloud service integrations.',
      highlights: [
        '51 page routes and 26 API endpoints covering every aspect of pickleball strategy, technique, and equipment',
        '1,014 paddles from 92 brands with card/table/radar views, multi-axis filters, side-by-side comparison, and 4 sources',
        '266 PPA pro athlete profiles with DUPR, ELO, win rates, archetype classification, and gear data',
        '130+ drills across 11 categories with difficulty filters, impact ratings, video links, and save-to-collection',
        'Multi-model AI assistant (Gemini + Groq + Anthropic) with 4-tier caching via Pinecone vectors, Typesense full-text, Redis, and in-memory',
        '798 automated tests across Vitest unit tests and Playwright E2E suites',
        '15+ cloud service integrations: Supabase, Pinecone, Typesense, tRPC, Upstash Redis, PostHog, Sentry, Axiom, Trigger.dev, Cloudinary, and more',
        '46,458-line interactive digital magazine with 26 sections covering beginner fundamentals to professional-level strategy',
        'Zero charting libraries — every visualization (radar charts, bar charts, court diagrams, performance gauges) is hand-coded SVG',
        'Interactive paddle lab with clock-face weight simulator, real-time physics (swing wt, twist wt, balance), and 3D rotation',
        'Tactical whiteboard with drag-and-drop players, 7 drawing tools, shot stamps, and undo/redo',
        'Live PPA tournament data with match tickers, brackets, rankings across 6 categories, and event schedules',
      ],
      techDetails: 'Next.js 16 App Router with React 19, TypeScript, Tailwind CSS v4. Backend powered by tRPC, Supabase (PostgreSQL), Pinecone vector search (6,590 vectors), and Typesense full-text search. Upstash Redis for caching and rate limiting. BetterAuth for authentication. Observability via PostHog, Sentry, and Axiom. Background jobs via Trigger.dev (16 tasks). 36 data pipeline scripts. 798 automated tests with Vitest, Playwright, Storybook, and Chromatic. Deployed on Vercel.',
    },
  },
];
